/**
 * ⚠️ 临时探针 —— 主题切换专项（排查结束后连同本文件、以及 TabLayout 里对它的
 *    三处调用一起删除）。与 utils/navPerfProbe.ts 同一风格：生产环境整体不启用。
 *
 * 为什么单独做一个：navPerfProbe 只观察「路由切换」，而本问题的症状是
 * 「切主题时几个大模块各自切换 + 卡顿」，要回答的是**同一帧里到底谁先谁后**。
 *
 * 开关（改完立即生效，无需刷新，与 navPerf 同款）：
 *     __themeSwitch.enable()          或 localStorage.themePerf = '1'
 *     点主题开关（月亮/太阳）一次 → 自动打印一条样本
 *     __themeSwitch.report()          再打一次全部样本
 *     __themeSwitch.dump()            取可整段复制的 JSON
 *
 * 每条样本的字段：
 *   点击→根属性ms   点击 → <html data-theme> 已写好的耗时。
 *                   这是「CSS 变量面」的翻转时刻，应当 ≈0（同步写）。
 *                   若这里出现几十上百 ms，说明点击路径上还有东西挡着。
 *   点击→首帧ms     点击 → 翻转后第一次绘制（双 rAF）。这一段是「React 提交 +
 *                   样式重算 + 布局 + 绘制」的总和 = 用户实际感到的卡顿。
 *   长任务数/总时长/最长  观察窗口（默认 1.2s）内 >50ms 长任务的统计。
 *                   长任务多且长 = 主线程被堵住，切换不跟手。
 *   分身翻转        其它元素上 data-*theme 属性的写入时刻（相对点击的 ms）。
 *                   ★ 这是「几个大模块各自切换」的**直接证据**：只要这张表非空，
 *                     就说明有模块的颜色不是由 <html data-theme> 统一驱动的，
 *                     而是在 React 提交之后各自翻一次。
 *                     收敛完成后这张表应当**只剩 <html data-theme> 一条**。
 *   过渡闸门        切换瞬间 <html> 上是否带着 .theme-flipping。
 *                   false = 守卫没生效，各模块的颜色过渡会各自跑，观感即「各切各的」。
 *   规模            DOM 节点数 / keep-alive 缓存页签数（背景信息：提交成本随这两个涨）。
 *
 * 运行：dev server 里点一次主题开关，控制台即出表。
 */

type Offender = {
  /** 元素的可读标识：取 id / 首个 class / 标签名 */
  el: string;
  attr: string;
  value: string;
  /** 相对点击的毫秒数 */
  atMs: number;
};

type Sample = {
  seq: number;
  /** 点击 → <html data-theme> 写好 */
  attrMs: number | null;
  /** 点击 → 翻转后首帧（双 rAF） */
  paintMs: number | null;
  longTaskCount: number;
  longTaskTotalMs: number;
  longTaskMaxMs: number;
  offenders: Offender[];
  /** 切换那一刻 html 上是否有 .theme-flipping 守卫 */
  guardOn: boolean;
  domNodes: number;
  kaEntries: number;
  /** 采样时 <html data-theme> 的值 */
  theme: string;
};

const WINDOW_MS = 1200;

let enabled = false;
let seq = 0;
let t0: number | null = null;
let current: Sample | null = null;
let rootObserver: MutationObserver | null = null;
let longTaskObserver: PerformanceObserver | null = null;
let settleTimer: number | undefined;
const samples: Sample[] = [];

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function readEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return (
      window.localStorage.getItem('themePerf') === '1' ||
      window.location.search.includes('__themeperf')
    );
  } catch {
    return false;
  }
}

/** 把元素压成可读标识，控制台里能一眼认出是哪个模块 */
function describe(el: Element): string {
  const id = el.id ? `#${el.id}` : '';
  const cls = String(el.className || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((c) => `.${c}`)
    .join('');
  return `${el.tagName.toLowerCase()}${id}${cls}` || el.tagName.toLowerCase();
}

function stopObservers(): void {
  rootObserver?.disconnect();
  rootObserver = null;
  longTaskObserver?.disconnect();
  longTaskObserver = null;
  if (settleTimer !== undefined) {
    window.clearTimeout(settleTimer);
    settleTimer = undefined;
  }
}

function settle(): void {
  if (!current) return;
  stopObservers();
  samples.push(current);
  current = null;
  report();
}

/** 兜底：探针模块自身出错绝不能影响主题切换 */
function safe(fn: () => void): void {
  try {
    fn();
  } catch (_e) {
    /* ignore */
  }
}

export const themeSwitchProbe = {
  /** toggleTheme 的**第一行**调用：开始记时 + 挂观察者 */
  begin(): void {
    if (process.env.NODE_ENV === 'production') return;
    safe(() => {
      enabled = readEnabled();
      if (!enabled || typeof document === 'undefined') return;
      stopObservers();
      t0 = now();
      current = {
        seq: ++seq,
        attrMs: null,
        paintMs: null,
        longTaskCount: 0,
        longTaskTotalMs: 0,
        longTaskMaxMs: 0,
        offenders: [],
        guardOn: false,
        domNodes: document.getElementsByTagName('*').length,
        kaEntries: 0,
        theme: '',
      };

      // 1) 盯住「谁在翻主题属性」——整棵文档子树的属性写入，只看 *theme 类属性
      rootObserver = new MutationObserver((records) => {
        if (!current || t0 == null) return;
        for (const rec of records) {
          const name = rec.attributeName || '';
          if (!/theme/i.test(name)) continue;
          const el = rec.target as Element;
          const isRoot = el === document.documentElement;
          const value = el.getAttribute(name) ?? '(removed)';
          const atMs = now() - t0;
          if (isRoot && name === 'data-theme') {
            current.attrMs ??= atMs;
            current.theme = value;
          }
          // 根属性不是「分身」——分身只记其它元素/其它属性上的主题标记
          if (isRoot && name === 'data-theme') continue;
          current.offenders.push({ el: describe(el), attr: name, value, atMs });
        }
      });
      rootObserver.observe(document.documentElement, {
        attributes: true,
        subtree: true,
      });

      // 2) 长任务：观察窗口内主线程被堵住的规模
      try {
        longTaskObserver = new PerformanceObserver((list) => {
          if (!current) return;
          for (const entry of list.getEntries()) {
            current.longTaskCount += 1;
            current.longTaskTotalMs += entry.duration;
            current.longTaskMaxMs = Math.max(current.longTaskMaxMs, entry.duration);
          }
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });
      } catch (_e) {
        /* 浏览器不支持 longtask，其余字段仍可用 */
      }
    });
  },

  /** setInitialState 之后调用：记下守卫状态并启动「首帧」打点与收尾定时器 */
  end(kaEntries = 0): void {
    if (process.env.NODE_ENV === 'production') return;
    safe(() => {
      if (!enabled || !current || t0 == null) return;
      current.kaEntries = kaEntries;
      current.guardOn = document.documentElement.classList.contains('theme-flipping');
      const start = t0;
      const s = current;
      // 双 rAF：第二次回调落在「翻转已经绘制出来」之后
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (s && s.paintMs == null) s.paintMs = now() - start;
        });
      });
      settleTimer = window.setTimeout(settle, WINDOW_MS);
    });
  },
};

function report(): void {
  if (!samples.length) {
    // eslint-disable-next-line no-console
    console.info('[themeSwitch] 还没有样本。先 __themeSwitch.enable()，再点一次主题开关。');
    return;
  }
  // eslint-disable-next-line no-console
  console.table(
    samples.map((s) => ({
      序: s.seq,
      点击到根属性ms: s.attrMs?.toFixed(1) ?? '-',
      点击到首帧ms: s.paintMs?.toFixed(1) ?? '-',
      长任务数: s.longTaskCount,
      长任务总计ms: s.longTaskTotalMs.toFixed(1),
      最长任务ms: s.longTaskMaxMs.toFixed(1),
      分身翻转数: s.offenders.length,
      过渡闸门: s.guardOn ? '开' : '关',
      主题: s.theme,
      DOM节点: s.domNodes,
      缓存页签: s.kaEntries,
    })),
  );
  const offenders = samples.flatMap((s) =>
    s.offenders.map((o) => ({
      序: s.seq,
      元素: o.el,
      属性: o.attr,
      值: o.value,
      距点击ms: o.atMs.toFixed(1),
    })),
  );
  if (offenders.length) {
    // eslint-disable-next-line no-console
    console.info(
      '[themeSwitch] ★ 分身翻转明细（这些模块的主题不是由 <html data-theme> 统一驱动的）：',
    );
    // eslint-disable-next-line no-console
    console.table(offenders);
  } else {
    // eslint-disable-next-line no-console
    console.info('[themeSwitch] 无分身翻转：全部主题面都由 <html data-theme> 统一驱动。');
  }
}

/** 无论开关与否都挂上，控制台随时可用（生产不安装） */
function defineGlobal(): void {
  if (process.env.NODE_ENV === 'production') return;
  if (typeof window === 'undefined') return;
  (window as any).__themeSwitch = {
    get enabled() {
      return readEnabled();
    },
    samples,
    enable() {
      try {
        window.localStorage.setItem('themePerf', '1');
      } catch (_e) {
        /* ignore */
      }
      // eslint-disable-next-line no-console
      console.info('[themeSwitch] 已开始采集，请点一次主题开关（月亮/太阳）。');
    },
    disable() {
      try {
        window.localStorage.removeItem('themePerf');
      } catch (_e) {
        /* ignore */
      }
      // eslint-disable-next-line no-console
      console.info('[themeSwitch] 已停止采集。');
    },
    report,
    clear() {
      samples.length = 0;
    },
    dump: () => JSON.stringify(samples, null, 2),
  };
}

defineGlobal();

export default themeSwitchProbe;